import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  ImageBackground,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AmbientGlow from '../components/AmbientGlow';
import { auth, FIRESTORE_COLLECTIONS } from '../firebase';
import { useDayVibes } from '../hooks/useDayVibes';
import { DAY_VIBES_QUESTIONS, Question } from '../data/dayVibesQuestions';
import {
  DAILY_DAY_VIBE_COUNT,
  DailyDayVibeQuestion,
  fetchDailyDayVibeQuestions,
  getDayOffset,
  hashString,
  seededShuffle,
} from '../utils/dayVibes';
import { toScreenQuestion } from '../utils/dayVibeScoring';
import type { RootStackParamList } from '../types';
import { Typography, Radius } from '../theme';
import { useI18n } from '../i18n';
import { getTranslatedText } from '../utils/translations';
import { VIBE_Q_TRANSLATIONS } from '../i18n/vibeQuestionTranslations';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';

const BACKGROUND_IMAGE = require('../assets/BG.jpg');

function getFallbackQuestions(dateKey: string): Question[] {
  const dayOffset = getDayOffset(dateKey);
  const cycleSize = DAY_VIBES_QUESTIONS.length;
  const dailyCount = Math.min(DAILY_DAY_VIBE_COUNT, cycleSize);

  // See getDailyDayVibeQuestions in utils/dayVibes.ts for the full rationale: a
  // day's block is always sliced from a single cycle's shuffled permutation, so
  // it can never straddle two independently-shuffled cycles and repeat a
  // question before every question in the pool has been asked once.
  const daysPerCycle = Math.max(1, Math.floor(cycleSize / dailyCount));
  const cycleIndex = Math.floor(dayOffset / daysPerCycle);
  const dayInCycle = dayOffset % daysPerCycle;
  const cyclePool = seededShuffle(DAY_VIBES_QUESTIONS, hashString(`day-vibes-fallback-cycle:${cycleIndex}`));
  const startIndex = dayInCycle * dailyCount;
  const selectedQuestions = cyclePool.slice(startIndex, startIndex + dailyCount);

  return seededShuffle(selectedQuestions, hashString(`day-vibes-fallback-daily-order:${dateKey}`));
}

export default function DayVibesQuestionScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { language, t, tp } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const uid = auth.currentUser?.uid ?? null;
  const { state, loading, dateKey, draftAnswers, draftQuestionIndex, saveDraft, submitAnswers } = useDayVibes(uid);

  const [questions, setQuestions] = useState<Question[]>(DAY_VIBES_QUESTIONS);
  const [rawDailyQuestions, setRawDailyQuestions] = useState<DailyDayVibeQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [translationsMap, setTranslationsMap] = useState<Record<string, { qText: string; optMap: Record<string, string> }>>({});
  const [translatingAll, setTranslatingAll] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const total = questions.length;

  useEffect(() => {
    let cancelled = false;
    setLoadingQuestions(true);
    setRawDailyQuestions([]);
    setTranslationsMap({});
    fetchDailyDayVibeQuestions(dateKey)
      .then(dailyQuestions => {
        if (cancelled) return;
        if (dailyQuestions.length) {
          setRawDailyQuestions(dailyQuestions);
          setQuestions(dailyQuestions.map(q => toScreenQuestion(q, t)));
        } else {
          // This was previously silent, which made it impossible to tell from
          // logs whether the live 500-question Firestore pool was ever actually
          // reached — if this fires in production, the app is running on the
          // 15-question local fallback, which is a much smaller no-repeat window.
          console.warn('[Tonyte][DayVibes] Firestore question set was empty; using local fallback pool', { dateKey });
          setQuestions(getFallbackQuestions(dateKey));
        }
      })
      .catch(error => {
        console.warn('[Tonyte][DayVibes] failed to load daily question set', { dateKey, error });
        if (!cancelled) setQuestions(getFallbackQuestions(dateKey));
      })
      .finally(() => {
        if (!cancelled) setLoadingQuestions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateKey, t]);

  useEffect(() => {
    const hasDraft = Object.keys(draftAnswers).length > 0;
    setAnswers(hasDraft ? draftAnswers : {});
    setQuestionIndex(hasDraft ? Math.min(draftQuestionIndex, Math.max(questions.length - 1, 0)) : 0);
  }, [dateKey, draftAnswers, draftQuestionIndex, questions.length]);

  // Lock guard: if a submitted response already exists, redirect immediately
  useEffect(() => {
    if (loading) return;
    if (state === 'submitted_waiting' || state === 'submitted_unlocked') {
      navigation.replace('DayVibesAura');
    }
  }, [loading, state, navigation]);

  // Animations
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const pulseAnims = useRef<Record<string, Animated.Value>>({}).current;

  const question = questions[questionIndex] ?? DAY_VIBES_QUESTIONS[0];
  const selected = answers[question.id] ?? [];
  const isMulti = question.type === 'multi';
  const maxSel = question.maxSelections ?? 1;
  const hasAnswer = selected.length > 0;
  const isLast = questionIndex === total - 1;
  const scrollPaddingBottom = insets.bottom + 24;

  // Translate all questions when the stable question set is ready and language != 'en'.
  // Two-path strategy:
  //   1. INSTANT: pre-bundled translations in VIBE_Q_TRANSLATIONS (no network, no CF)
  //   2. ASYNC CF: for Firestore questions not covered by pre-bundled set, calls
  //      getTranslatedText (Firestore-cached on first hit, instant on return visits).
  //      CF results update the map without blocking the UI.
  useEffect(() => {
    if (language === 'en') {
      setTranslationsMap({});
      setTranslatingAll(false);
      return;
    }
    // Wait until questions have finished loading so we don't translate the wrong set.
    if (loadingQuestions) return;
    if (questions.length === 0) return;

    // ── Step 1: populate from pre-bundled translations immediately ────────────
    const bundled: Record<string, { qText: string; optMap: Record<string, string> }> = {};
    questions.forEach(q => {
      const b = VIBE_Q_TRANSLATIONS[q.id]?.[language];
      if (b) {
        bundled[q.id] = { qText: b.question, optMap: { ...b.options } };
      }
    });
    // Set instantly — user sees German immediately for any pre-bundled question.
    setTranslationsMap(bundled);

    // ── Step 2: async CF for questions not in the pre-bundled set ─────────────
    const hasFirestore = rawDailyQuestions.length > 0;
    type Job = { id: string; questionText: string; optionTexts: string[] };
    const cfJobs: Job[] = hasFirestore
      ? rawDailyQuestions
          .filter(rawQ => !VIBE_Q_TRANSLATIONS[rawQ.id]?.[language])
          .map(rawQ => ({ id: rawQ.id, questionText: rawQ.question, optionTexts: rawQ.options }))
      : questions
          .filter(q => !VIBE_Q_TRANSLATIONS[q.id]?.[language])
          .map(q => ({ id: q.id, questionText: q.text, optionTexts: q.options.map(o => o.text) }));

    if (cfJobs.length === 0) {
      setTranslatingAll(false);
      return;
    }

    let cancelled = false;
    setTranslatingAll(true);

    Promise.all(cfJobs.map(async ({ id, questionText, optionTexts }) => {
      const joinedOptions = optionTexts.join('|||');
      const [translatedQ, translatedOptsStr] = await Promise.all([
        getTranslatedText({
          collectionName: FIRESTORE_COLLECTIONS.dayVibeQuestions,
          docId: id,
          field: 'question',
          originalText: questionText,
          targetLanguage: language,
          sourceLanguage: 'en',
          context: 'Day Vibes personality question in a social events app. Short and conversational.',
        }).catch(() => questionText),
        getTranslatedText({
          collectionName: FIRESTORE_COLLECTIONS.dayVibeQuestions,
          docId: id,
          field: 'options',
          originalText: joinedOptions,
          targetLanguage: language,
          sourceLanguage: 'en',
          context: 'Day Vibes answer choices separated by |||. Translate each concisely. Keep the ||| separators exactly as-is.',
        }).catch(() => joinedOptions),
      ]);
      const parts = translatedOptsStr.split('|||').map(s => s.trim());
      const optMap: Record<string, string> = {};
      optionTexts.forEach((text, i) => { if (parts[i]) optMap[text] = parts[i]; });
      return [id, { qText: translatedQ, optMap }] as const;
    })).then(entries => {
      if (!cancelled) {
        setTranslationsMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    }).finally(() => {
      if (!cancelled) setTranslatingAll(false);
    });

    return () => { cancelled = true; };
  }, [rawDailyQuestions, questions, loadingQuestions, language]);

  const getPulseAnim = (optionId: string): Animated.Value => {
    if (!pulseAnims[optionId]) {
      pulseAnims[optionId] = new Animated.Value(1);
    }
    return pulseAnims[optionId];
  };

  const animatePulse = (optionId: string) => {
    const anim = getPulseAnim(optionId);
    Animated.sequence([
      Animated.timing(anim, { toValue: 0.94, duration: 80, useNativeDriver: true }),
      Animated.spring(anim, { toValue: 1, useNativeDriver: true, speed: 22, bounciness: 6 }),
    ]).start();
  };

  const transitionQuestion = useCallback((newIndex: number, direction: 'forward' | 'back') => {
    const exitX = direction === 'forward' ? -28 : 28;
    const enterX = direction === 'forward' ? 28 : -28;
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 160, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: exitX, duration: 160, useNativeDriver: true }),
    ]).start(() => {
      slideAnim.setValue(enterX);
      setQuestionIndex(newIndex);
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      ]).start();
    });
  }, [fadeAnim, slideAnim]);

  const selectOption = (optionId: string) => {
    animatePulse(optionId);
    setAnswers(prev => {
      const current = prev[question.id] ?? [];
      let next: string[];
      if (isMulti) {
        if (current.includes(optionId)) {
          next = current.filter(id => id !== optionId);
        } else if (current.length < maxSel) {
          next = [...current, optionId];
        } else {
          next = current;
        }
      } else {
        next = [optionId];
      }
      return { ...prev, [question.id]: next };
    });
  };

  const goNext = async () => {
    if (!hasAnswer || submitting) return;
    const updatedAnswers = { ...answers };
    if (isLast) {
      setSubmitting(true);
      const result = await submitAnswers(updatedAnswers, questions);
      setSubmitting(false);
      if (result) {
        navigation.replace('DayVibesAura');
      } else {
        Alert.alert(t('vibes.couldNotSaveVibe'), t('vibes.couldNotSaveVibeSub'));
      }
      return;
    }
    const nextIndex = questionIndex + 1;
    saveDraft(updatedAnswers, nextIndex);
    transitionQuestion(nextIndex, 'forward');
  };

  const goBack = () => {
    if (questionIndex === 0) {
      navigation.goBack();
      return;
    }
    const prevIndex = questionIndex - 1;
    saveDraft(answers, prevIndex);
    transitionQuestion(prevIndex, 'back');
  };

  const bottomActions = (
    <>
      <View style={styles.bottomRow}>
        <TouchableOpacity
          onPress={goBack}
          style={[styles.backAction, appearance.backAction]}
          activeOpacity={0.72}
        >
          <Icon name="chevron-left" size={18} color={theme.isDark ? 'rgba(255,255,255,0.48)' : theme.colors.iconMuted} />
          <Text style={[styles.backActionText, appearance.backActionText]}>
            {questionIndex === 0 ? t('common.cancel') : t('common.back')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={goNext}
          disabled={!hasAnswer || submitting}
          activeOpacity={0.88}
          style={[
            styles.nextBtn,
            !theme.isDark && styles.nextBtnDaylight,
            (!hasAnswer || submitting) && styles.nextBtnDisabled,
            !theme.isDark && (!hasAnswer || submitting) && styles.nextBtnDaylightDisabled,
          ]}
        >
          {theme.isDark ? (
            <LinearGradient
              colors={
                hasAnswer && !submitting
                  ? isLast
                    ? ['#C084FC', '#818CF8', '#6366F1']
                    : ['#F27D26', '#E06214']
                  : ['rgba(255,255,255,0.06)', 'rgba(255,255,255,0.04)']
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
          ) : null}
          {submitting ? (
            <View style={styles.nextBtnContent}>
              <View style={styles.loadingDotRow}>
                {[0, 1, 2].map(i => (
                  <View key={i} style={styles.loadingDot} />
                ))}
              </View>
            </View>
          ) : (
            <View style={styles.nextBtnContent}>
              <Text style={[styles.nextBtnText, !hasAnswer && styles.nextBtnTextDisabled, !hasAnswer && appearance.nextBtnTextDisabled]}>
                {isLast ? t('vibes.synchronize') : t('vibes.next')}
              </Text>
              <Icon
                name={isLast ? 'zap' : 'arrow-right'}
                size={16}
                color={hasAnswer ? '#fff' : (theme.isDark ? 'rgba(255,255,255,0.28)' : theme.colors.iconMuted)}
              />
            </View>
          )}
        </TouchableOpacity>
      </View>

      <Text style={[styles.progressLabel, appearance.progressLabel]}>
        {total - questionIndex - 1 === 0
          ? t('vibes.lastOne')
          : tp('vibes.questionLeftCount', total - questionIndex - 1)}
      </Text>
    </>
  );

  // Show spinner while loading. translatingAll only blocks when CF work is in flight
  // for Firestore questions not covered by pre-bundled translations.
  if (loading || loadingQuestions || translatingAll) {
    return (
      <View style={[styles.container, appearance.container, { alignItems: 'center', justifyContent: 'center' }]}> 
        <ActivityIndicator color="#C084FC" size="large" />
      </View>
    );
  }

  if (state === 'locked_until_open') {
    return (
      <ImageBackground source={BACKGROUND_IMAGE} style={[styles.container, appearance.container]} resizeMode="cover">
        <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
        <LinearGradient
          colors={theme.isDark
            ? ['rgba(2,5,13,0.74)', 'rgba(7,8,22,0.9)', 'rgba(3,5,12,0.98)']
            : ['rgba(245,240,232,0.94)', 'rgba(248,241,231,0.97)', 'rgba(245,240,232,0.99)']}
          locations={[0, 0.46, 1]}
          style={StyleSheet.absoluteFill}
        />
        <AmbientGlow tone="orange" size={320} intensity={0.28} style={styles.glowTopLeft} />
        <AmbientGlow tone="teal" size={260} intensity={0.22} style={styles.glowMid} />
        <AmbientGlow tone="blue" size={300} intensity={0.24} style={styles.glowBottomRight} />
        <View style={styles.particleA} />
        <View style={styles.particleB} />
        <View style={styles.particleD} />
        <View style={[styles.lockedState, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 24 }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={[styles.lockedBackBtn, appearance.backBtn]} activeOpacity={0.7}>
            <Icon name="arrow-left" size={17} color={theme.isDark ? 'rgba(255,255,255,0.58)' : theme.colors.icon} />
          </TouchableOpacity>

          <View style={[styles.lockedStateCard, appearance.lockedStateCard]}>
            <LinearGradient
              colors={theme.isDark
                ? ['rgba(255,255,255,0.14)', 'rgba(15,21,32,0.84)', 'rgba(5,8,18,0.96)']
                : ['rgba(255,255,255,0.84)', 'rgba(255,249,239,0.94)', 'rgba(242,229,211,0.98)']}
              locations={[0, 0.42, 1]}
              style={StyleSheet.absoluteFill}
            />
            <LinearGradient
              colors={['rgba(255,179,71,0.16)', 'rgba(38,208,206,0.10)', 'rgba(192,132,252,0.10)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />

            <View style={styles.lockedBadge}>
              <Icon name="sunrise" size={11} color="rgba(255,213,128,0.9)" />
              <Text style={styles.lockedBadgeText}>{t('vibes.dayVibesBadge')}</Text>
            </View>

            <View style={styles.lockedOrbWrap}>
              <View style={styles.lockedOrbitOuter} />
              <View style={styles.lockedOrbitInner} />
              <LinearGradient
                colors={['#FFB347', '#C084FC', '#26D0CE']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.lockedOrb}
              >
                <View style={styles.lockedOrbCore}>
                  <Icon name="lock" size={26} color="rgba(255,255,255,0.9)" />
                </View>
              </LinearGradient>
            </View>

            <Text style={[styles.lockedTitle, appearance.textPrimary]}>{t('vibes.openAt6am')}</Text>
            <Text style={[styles.lockedSubtitle, appearance.textSecondary]}>{t('vibes.resetDaily')}</Text>

            <View style={styles.lockedRail}>
              <View style={styles.lockedRailItem}>
                <View style={styles.lockedRailDotMuted} />
                <Text style={styles.lockedRailText}>{t('vibes.reset')}</Text>
              </View>
              <View style={styles.lockedRailLine} />
              <View style={styles.lockedRailItem}>
                <View style={styles.lockedRailDotActive}>
                  <Icon name="clock" size={9} color="#07101d" />
                </View>
                <Text style={styles.lockedRailTextActive}>{t('discover.auraOpenTime')} {t('discover.am')}</Text>
              </View>
              <View style={styles.lockedRailLine} />
              <View style={styles.lockedRailItem}>
                <View style={styles.lockedRailDotMuted} />
                <Text style={styles.lockedRailText}>{t('vibes.scan')}</Text>
              </View>
            </View>
          </View>
        </View>
      </ImageBackground>
    );
  }

  return (
    <View style={[styles.container, appearance.container]}>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />

      {/* Background atmosphere */}
      <LinearGradient
        colors={theme.isDark
          ? ['#050817', '#080B24', '#130B22']
          : ['rgba(245,240,232,0.94)', 'rgba(248,241,231,0.97)', 'rgba(245,240,232,0.99)']}
        locations={[0, 0.46, 1]}
        style={StyleSheet.absoluteFill}
      />
      <AmbientGlow tone="blue" size={320} intensity={0.38} style={styles.glowTopLeft} />
      <AmbientGlow tone="orange" size={320} intensity={0.28} style={styles.glowBottomRight} />
      <AmbientGlow tone="teal" size={260} intensity={0.22} style={styles.glowMid} />
      <View style={styles.abstractBandLeft} />
      <View style={styles.abstractBandRight} />
      <View style={styles.headerAuraGlow} />
      <LinearGradient
        colors={['rgba(4,7,17,0.08)', 'rgba(4,7,17,0)']}
        style={[styles.headerFade, appearance.headerFade]}
      />
      <View style={styles.particleA} />
      <View style={styles.particleB} />
      <View style={styles.particleC} />
      <View style={styles.particleD} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          {
            paddingTop: insets.top + 6,
            paddingBottom: scrollPaddingBottom,
          },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.gamePanel, appearance.gamePanel]}>
        {/* Header row */}
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={goBack} style={[styles.backBtn, appearance.backBtn]} activeOpacity={0.7}>
            <Icon name="arrow-left" size={17} color={theme.isDark ? 'rgba(255,255,255,0.58)' : theme.colors.icon} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <View style={[styles.dayVibesBadge, appearance.dayVibesBadge]}>
              <Icon name="sun" size={10} color={theme.isDark ? 'rgba(255,179,71,0.86)' : '#B75B0D'} />
              <Text style={[styles.dayVibesLabel, appearance.dayVibesLabel]}>{t('vibes.dayVibesBadge')}</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={[styles.questionCount, appearance.textMuted]}>
              {questionIndex + 1} / {total}
            </Text>
          </View>
        </View>

        <View style={styles.progressBlock}>
          <View style={[styles.progressTrack, appearance.progressTrack]}>
            {questions.map((_, i) => (
              <LinearGradient
                key={i}
                style={[
                  styles.progressSegment,
                  i < questionIndex && styles.progressSegmentDone,
                ]}
                colors={i === questionIndex
                  ? ['#43E7F4', '#AE74FF', '#FF9A46']
                  : i < questionIndex
                    ? theme.isDark
                      ? ['rgba(255,255,255,0.36)', 'rgba(255,255,255,0.3)']
                      : ['rgba(103,91,78,0.38)', 'rgba(103,91,78,0.28)']
                    : theme.isDark
                      ? ['rgba(255,255,255,0.15)', 'rgba(255,255,255,0.1)']
                      : ['rgba(103,91,78,0.16)', 'rgba(103,91,78,0.10)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
              />
            ))}
          </View>
        </View>

        {/* Question card (animated) */}
        <Animated.View
          style={[
            styles.questionCard,
            appearance.questionCard,
            { opacity: fadeAnim, transform: [{ translateX: slideAnim }] },
          ]}
        >
          {theme.isDark ? (
            <>
              <LinearGradient
                colors={['rgba(255,255,255,0.13)', 'rgba(15,21,32,0.9)', 'rgba(5,8,18,0.94)']}
                locations={[0, 0.34, 1]}
                style={[StyleSheet.absoluteFill, styles.questionCardGradient]}
              />
              <LinearGradient
                colors={['rgba(38,208,206,0.15)', 'rgba(192,132,252,0.10)', 'rgba(242,125,38,0.08)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
            </>
          ) : null}
          {/* Neon border accent */}
          <View style={[styles.cardBorderAccent, appearance.cardBorderAccent]} />
          <View style={[styles.questionHalo, appearance.questionHalo]} />

          <View style={styles.questionMeta}>
            <View style={[styles.questionNumberPill, appearance.questionNumberPill]}>
              <Text style={[styles.questionNumber, appearance.questionNumber]}>Q{questionIndex + 1}</Text>
            </View>
          </View>
          <Text style={[styles.questionText, appearance.questionText]}>{translationsMap[question.id]?.qText || question.text}</Text>
          {isMulti ? (
            <Text style={[styles.multiHint, appearance.multiHint]}>
              {t('vibes.pickAllHint')} · {t('vibes.pickUpTo')} {maxSel}.
            </Text>
          ) : (
            <Text style={[styles.singleHint, appearance.textSecondary]}>{t('vibes.dontOverthink')}</Text>
          )}
        </Animated.View>

        {/* Options */}
        <Animated.View
          style={[styles.optionsList, { opacity: fadeAnim, transform: [{ translateX: slideAnim }] }]}
        >
          {question.options.map((opt, optionIndex) => {
            const isSelected = selected.includes(opt.id);
            const pulseAnim = getPulseAnim(opt.id);
            const isAtMax = isMulti && selected.length >= maxSel && !isSelected;

            return (
              <Animated.View key={opt.id} style={{ transform: [{ scale: pulseAnim }] }}>
                <TouchableOpacity
                  onPress={() => !isAtMax && selectOption(opt.id)}
                  activeOpacity={0.82}
                  style={[
                    styles.optionBtn,
                    appearance.optionBtn,
                    isSelected && styles.optionBtnSelected,
                    isSelected && appearance.optionBtnSelected,
                    isAtMax && styles.optionBtnDimmed,
                  ]}
                >
                  {theme.isDark && isSelected ? (
                    <LinearGradient
                      colors={['rgba(242,125,38,0.30)', 'rgba(137,58,71,0.18)', 'rgba(55,24,27,0.24)']}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                      style={[StyleSheet.absoluteFill, { borderRadius: 18 }]}
                    />
                  ) : null}
                  {theme.isDark ? (
                    <LinearGradient
                      colors={['rgba(255,255,255,0.08)', 'rgba(255,255,255,0)']}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={styles.optionSheen}
                    />
                  ) : null}
                  <View style={styles.optionInner}>
                    <View style={[styles.optionIndex, isSelected && styles.optionIndexSelected]}>
                      <Text style={[styles.optionIndexText, appearance.textMuted, isSelected && styles.optionIndexTextSelected]}>{optionIndex + 1}</Text>
                    </View>
                    <View style={styles.optionCopy}>
                      <Text style={[styles.optionText, appearance.optionText, isSelected && styles.optionTextSelected, isSelected && appearance.optionTextSelected]}>
                        {translationsMap[question.id]?.optMap[opt.text] || opt.text}
                      </Text>
                    </View>
                    {isSelected && (
                      <LinearGradient colors={['#FFAD58', '#F26B19']} style={styles.optionCheck}>
                        <Icon name="check" size={15} color="#fff" />
                      </LinearGradient>
                    )}
                  </View>
                </TouchableOpacity>
              </Animated.View>
            );
          })}
        </Animated.View>

        {bottomActions}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#070b14' },
  scroll: { flex: 1 },
  content: {
    paddingHorizontal: 10,
    flexGrow: 1,
  },
  glowTopLeft: { position: 'absolute', top: -140, left: -120 },
  glowBottomRight: { position: 'absolute', bottom: -160, right: -100 },
  glowMid: { position: 'absolute', top: 190, right: -130 },
  abstractBandLeft: {
    position: 'absolute',
    left: -250,
    top: 170,
    width: 430,
    height: 520,
    borderRadius: 220,
    borderWidth: 3,
    borderColor: 'rgba(38,208,206,0.34)',
    transform: [{ rotate: '-24deg' }],
  },
  abstractBandRight: {
    position: 'absolute',
    right: -270,
    bottom: -80,
    width: 440,
    height: 520,
    borderRadius: 230,
    borderWidth: 3,
    borderColor: 'rgba(242,125,38,0.38)',
    transform: [{ rotate: '20deg' }],
  },
  gamePanel: {
    gap: 12,
    paddingHorizontal: 13,
    paddingTop: 14,
    paddingBottom: 16,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.20)',
    backgroundColor: 'rgba(10,16,36,0.76)',
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 18 },
    elevation: 14,
  },
  headerAuraGlow: {
    position: 'absolute',
    top: 26,
    alignSelf: 'center',
    width: 142,
    height: 52,
    borderRadius: 32,
    backgroundColor: 'rgba(192,132,252,0.09)',
    shadowColor: '#26D0CE',
    shadowOpacity: 0.14,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 0 },
  },
  headerFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 132,
  },
  particleA: {
    position: 'absolute', top: 180, right: 44,
    width: 3, height: 3, borderRadius: 1.5,
    backgroundColor: 'rgba(192,132,252,0.8)',
  },
  particleB: {
    position: 'absolute', top: 320, left: 28,
    width: 2, height: 2, borderRadius: 1,
    backgroundColor: 'rgba(38,208,206,0.7)',
  },
  particleC: {
    position: 'absolute', top: 88, left: '55%',
    width: 2, height: 2, borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
  particleD: {
    position: 'absolute', bottom: 220, right: 60,
    width: 2, height: 2, borderRadius: 1,
    backgroundColor: 'rgba(252,211,77,0.6)',
  },

  // Header
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 2,
    marginBottom: 0,
  },
  backBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.025)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.055)',
  },
  headerCenter: { flex: 1, alignItems: 'center' },
  dayVibesBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(242,125,38,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,146,66,0.48)',
  },
  dayVibesLabel: {
    color: 'rgba(255,213,128,0.88)',
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.6,
  },
  questionCount: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  headerRight: { width: 54, alignItems: 'flex-end' },
  lockedState: {
    flex: 1,
    paddingHorizontal: 18,
    justifyContent: 'center',
    gap: 28,
  },
  lockedBackBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    shadowColor: '#000',
    shadowOpacity: 0.24,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
  },
  lockedStateCard: {
    minHeight: 390,
    borderRadius: 28,
    paddingHorizontal: 22,
    paddingVertical: 26,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    backgroundColor: 'rgba(15,21,32,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    overflow: 'hidden',
    shadowColor: '#26D0CE',
    shadowOpacity: 0.16,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 14 },
    elevation: 10,
  },
  lockedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,179,71,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,179,71,0.18)',
  },
  lockedBadgeText: {
    color: 'rgba(255,213,128,0.9)',
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.8,
  },
  lockedOrbWrap: {
    width: 150,
    height: 150,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockedOrbitOuter: {
    position: 'absolute',
    width: 148,
    height: 148,
    borderRadius: 74,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    transform: [{ rotate: '-12deg' }],
  },
  lockedOrbitInner: {
    position: 'absolute',
    width: 112,
    height: 112,
    borderRadius: 56,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    transform: [{ rotate: '18deg' }],
  },
  lockedOrb: {
    width: 92,
    height: 92,
    borderRadius: 46,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#C084FC',
    shadowColor: '#C084FC',
    shadowOpacity: 0.54,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 12,
  },
  lockedOrbCore: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(7,11,20,0.62)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  lockedTitle: {
    color: '#fff',
    fontSize: 24,
    lineHeight: 30,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  lockedSubtitle: {
    color: 'rgba(224,242,254,0.58)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: -10,
  },
  lockedRail: {
    width: '100%',
    minHeight: 66,
    borderRadius: 20,
    paddingHorizontal: 13,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(3,8,18,0.48)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  lockedRailItem: {
    width: 62,
    alignItems: 'center',
    gap: 7,
  },
  lockedRailLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.12)',
    marginBottom: 20,
  },
  lockedRailDotMuted: {
    width: 13,
    height: 13,
    borderRadius: 6.5,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  lockedRailDotActive: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFB347',
    shadowColor: '#FFB347',
    shadowOpacity: 0.5,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 0 },
  },
  lockedRailText: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: 10,
    fontWeight: Typography.bold,
  },
  lockedRailTextActive: {
    color: 'rgba(255,235,196,0.95)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
  },

  // Scan panel
  scanPanel: {
    minHeight: 62,
    borderRadius: 20,
    paddingHorizontal: 9,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: 'rgba(38,208,206,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.22)',
    shadowColor: '#26D0CE',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  },
  scanOrbWrap: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanOrb: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#C084FC',
    shadowColor: '#C084FC',
    shadowOpacity: 0.52,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 0 },
    elevation: 8,
  },
  scanOrbRing: {
    position: 'absolute',
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.32)',
  },
  scanCopy: { flex: 1, minWidth: 0 },
  scanLabel: {
    color: 'rgba(224,242,254,0.52)',
    fontSize: 11,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
  },
  scanText: {
    marginTop: 3,
    color: '#fff',
    fontSize: Typography.base,
    lineHeight: 20,
    fontWeight: Typography.extrabold,
  },
  scanPercent: {
    color: '#FFB347',
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
  },

  // Progress
  progressBlock: {
    paddingHorizontal: 1,
    paddingVertical: 1,
  },
  progressTrack: {
    height: 6,
    flexDirection: 'row',
    gap: 3,
    backgroundColor: 'transparent',
  },
  progressSegment: {
    flex: 1,
    height: 6,
    borderRadius: 999,
  },
  progressSegmentDone: {
    opacity: 0.9,
  },

  // Question card
  questionCard: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.20)',
    padding: 18,
    overflow: 'hidden',
    gap: 12,
    minHeight: 164,
    shadowColor: '#26D0CE',
    shadowOpacity: 0.18,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  questionCardGradient: { borderRadius: 26 },
  cardBorderAccent: {
    position: 'absolute',
    top: 0,
    left: 26,
    right: 26,
    height: 2,
    backgroundColor: 'rgba(38,208,206,0.62)',
  },
  questionHalo: {
    position: 'absolute',
    right: -42,
    top: -48,
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'rgba(192,132,252,0.13)',
  },
  questionMeta: { flexDirection: 'row', alignItems: 'center' },
  questionNumberPill: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
  },
  questionNumber: {
    color: '#99F6E4',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.4,
  },
  microPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    maxWidth: '68%',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  microCopy: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  questionText: {
    color: '#ffffff',
    fontSize: 26,
    fontWeight: Typography.extrabold,
    lineHeight: 32,
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 10,
  },
  singleHint: {
    color: 'rgba(226,232,240,0.64)',
    fontSize: Typography.sm,
    lineHeight: 18,
    fontWeight: Typography.semibold,
  },
  multiHint: {
    color: '#F5C889',
    fontSize: Typography.sm,
    lineHeight: 18,
    fontWeight: Typography.bold,
  },

  // Options
  optionsList: { gap: 11 },
  optionBtn: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(18,25,45,0.76)',
    paddingVertical: 13,
    paddingHorizontal: 14,
    overflow: 'hidden',
    minHeight: 64,
    justifyContent: 'center',
  },
  optionBtnSelected: {
    borderColor: 'rgba(255,157,66,0.90)',
    backgroundColor: 'rgba(61,28,29,0.80)',
    shadowColor: '#F27D26',
    shadowOpacity: 0.38,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 0 },
  },
  optionBtnDimmed: {
    opacity: 0.56,
  },
  optionSheen: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '46%',
  },
  optionInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  optionIndex: {
    width: 34,
    height: 34,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  optionIndexSelected: {
    borderColor: 'rgba(255,190,123,0.92)',
    backgroundColor: '#F27D26',
  },
  optionIndexText: {
    color: 'rgba(255,255,255,0.56)',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
  },
  optionIndexTextSelected: { color: '#fff' },
  optionCheck: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,205,150,0.82)',
  },
  optionCopy: { flex: 1, minWidth: 0 },
  optionText: {
    color: 'rgba(255,255,255,0.84)',
    fontSize: Typography.base,
    lineHeight: 20,
    fontWeight: Typography.semibold,
  },
  optionTextSelected: {
    color: '#ffffff',
    fontWeight: Typography.extrabold,
  },
  // Bottom
  iosBottomActions: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: 12,
    paddingHorizontal: 18,
    backgroundColor: 'rgba(3,5,12,0.96)',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.09)',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
    gap: 12,
  },
  backAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 96,
    minHeight: 52,
    justifyContent: 'center',
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  backActionText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  nextBtn: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    paddingVertical: 15,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    borderWidth: 1,
    borderColor: 'rgba(255,179,99,0.62)',
  },
  nextBtnDisabled: { opacity: 0.62, borderColor: 'rgba(255,255,255,0.10)' },
  nextBtnDaylight: { backgroundColor: '#F27D26' },
  nextBtnDaylightDisabled: { backgroundColor: 'rgba(255,252,247,0.54)', borderColor: 'rgba(134,112,88,0.16)' },
  nextBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  nextBtnText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
  nextBtnTextDisabled: {
    color: 'rgba(255,255,255,0.3)',
  },
  loadingDotRow: {
    flexDirection: 'row',
    gap: 6,
  },
  loadingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.5)',
  },

  progressLabel: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.22)',
    fontSize: Typography.xs,
    fontWeight: Typography.medium,
    marginTop: -4,
    paddingBottom: 8,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, {
    backgroundColor: theme.colors.background,
  }),
  backBtn: daylightStyle(theme, {
    backgroundColor: 'rgba(255,252,247,0.92)',
    borderColor: 'rgba(134,112,88,0.20)',
    ...theme.shadows.card,
  }),
  gamePanel: daylightStyle(theme, {
    backgroundColor: 'rgba(255,252,247,0.34)',
    borderColor: 'rgba(134,112,88,0.20)',
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  }),
  headerFade: daylightStyle(theme, {
    opacity: 0,
  }),
  lockedStateCard: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#8A5A32',
    shadowOpacity: 0.14,
  }),
  scanPanel: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.accentSecondaryBorder,
    ...theme.shadows.card,
  }),
  progressTrack: daylightStyle(theme, {
    backgroundColor: theme.colors.divider,
  }),
  questionCard: daylightStyle(theme, {
    backgroundColor: 'rgba(255,253,249,0.94)',
    borderColor: 'rgba(13,119,118,0.22)',
    shadowColor: '#8A5A32',
    shadowOpacity: 0.10,
    elevation: 4,
  }),
  cardBorderAccent: daylightStyle(theme, {
    backgroundColor: 'rgba(13,151,150,0.48)',
  }),
  questionHalo: daylightStyle(theme, {
    backgroundColor: 'rgba(192,132,252,0.055)',
  }),
  optionBtn: daylightStyle(theme, {
    backgroundColor: 'rgba(255,253,249,0.92)',
    borderColor: 'rgba(134,112,88,0.18)',
    shadowColor: '#75573A',
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 2,
  }),
  optionBtnSelected: daylightStyle(theme, {
    backgroundColor: 'rgba(255,231,205,0.96)',
    borderColor: '#F27D26',
    shadowColor: '#D96B1B',
    shadowOpacity: 0.14,
    elevation: 3,
  }),
  backAction: daylightStyle(theme, {
    backgroundColor: 'rgba(255,253,249,0.72)',
    borderColor: 'rgba(134,112,88,0.24)',
  }),
  iosBottomActions: daylightStyle(theme, {
    backgroundColor: 'rgba(248,241,231,0.97)',
    borderTopColor: theme.colors.divider,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  questionText: daylightStyle(theme, {
    color: theme.colors.textPrimary,
    textShadowColor: 'transparent',
  }),
  dayVibesBadge: daylightStyle(theme, {
    backgroundColor: 'rgba(196,112,22,0.10)',
    borderColor: 'rgba(177,91,11,0.30)',
  }),
  dayVibesLabel: daylightStyle(theme, {
    color: '#9A4F0C',
  }),
  questionNumberPill: daylightStyle(theme, {
    backgroundColor: 'rgba(21,155,154,0.12)',
    borderColor: 'rgba(13,119,118,0.34)',
  }),
  questionNumber: daylightStyle(theme, {
    color: '#0A7372',
  }),
  multiHint: daylightStyle(theme, {
    color: '#7B4AA8',
  }),
  optionText: daylightStyle(theme, { color: '#554C43' }),
  optionTextSelected: daylightStyle(theme, { color: '#3B2415' }),
  backActionText: daylightStyle(theme, { color: '#665C52' }),
  nextBtnTextDisabled: daylightStyle(theme, { color: theme.colors.textMuted }),
  progressLabel: daylightStyle(theme, { color: theme.colors.textMuted }),
});
